import type {
    ComponentStageData,
    ComponentStageHeuristicDefinition,
    MobileAlgoPlugin,
    MobileAlgoPluginInitializationArgs,
    PluginContext,
    PluginHeuristicsRegistry,
    PluginStageHandler
} from '../../types'
import {getContentBoxKey} from './common'
import {containerColumnsHeuristics} from './containerColumnsHeuristics'
import {
    findHeuristic,
    findHeuristicFromTree,
    getParent,
    createAdaptiveHeuristic,
    isHeaderFirstRowChildComponentSpecialCase,
    isGridContainerLayout,
    generatePluginHandlerPerMode
} from '../common'
import type {GridContainerLayout, GridItemLayout, Pointer, UnitSize} from '@wix/document-services-types'
import {AutoResponsivenessMode} from '../../../../constants/dsTypesConstants'
import {getMeasurementsPointer, getTargetLayoutPointer} from '../../conversionDal/utils/pointer'
import type {LayoutEntryItem} from '../../conversionDal/types'
import type {HorizontalScaleAndSizePluginAPI} from './types'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import _ from 'lodash'
import {containerColumnsHeuristicsAdaptive} from './containerColumnsHeuristicsAdaptive'
import {horizontalSpacingTree, widthTree} from './layoutModels'

export const createPlugin = ({stages, readOnlyExtensionAPI}: MobileAlgoPluginInitializationArgs): MobileAlgoPlugin => {
    const updateContentBoxByExistingLayout = (
        {componentPointer, conversionDal, config, globalContext}: ComponentStageData,
        contextHelper: PluginContext
    ) => {
        const contentBoxKey = getContentBoxKey(componentPointer)

        const parentPointer = getParent(conversionDal, componentPointer)
        const isBoxPlugin = conversionDal.get(componentPointer).semanticType === SemanticTypes.BOX_PLUGIN

        if (!parentPointer) {
            return contextHelper!.set(contentBoxKey, config.targetCanvasSize)
        }

        const contentBox = isBoxPlugin ? config.targetCanvasSize : contextHelper!.get(getContentBoxKey(parentPointer))

        const {componentLayout, itemLayout} = (conversionDal.get(getTargetLayoutPointer(componentPointer)) ??
            {}) as LayoutEntryItem
        const {containerLayout} = (conversionDal.get(getTargetLayoutPointer(parentPointer)) ?? {}) as LayoutEntryItem

        const calculateColumnsRatio = () => {
            if (
                !(itemLayout as GridItemLayout)?.gridArea?.columnStart ||
                !(itemLayout as GridItemLayout)?.gridArea?.columnEnd ||
                !(containerLayout as GridContainerLayout)?.columns?.length
            ) {
                return 1
            }

            return (
                ((itemLayout as GridItemLayout).gridArea.columnEnd -
                    (itemLayout as GridItemLayout).gridArea.columnStart) /
                (containerLayout as GridContainerLayout).columns.length
            )
        }

        if (
            isHeaderFirstRowChildComponentSpecialCase(
                componentPointer,
                conversionDal,
                globalContext,
                readOnlyExtensionAPI
            )
        ) {
            const measurements = conversionDal.get(getMeasurementsPointer(componentPointer))
            const {width} = measurements
            return contextHelper!.set(contentBoxKey, width)
        }

        const currentWidth = (componentLayout?.width as UnitSize)?.value ?? 100
        contextHelper!.set(contentBoxKey, (currentWidth / 100) * (contentBox / calculateColumnsRatio()))
    }

    const setContainerColumns =
        (columnsHeuristics: ComponentStageHeuristicDefinition[]): PluginStageHandler<ComponentStageData> =>
        (componentStageData: ComponentStageData, contextHelper?: PluginContext) => {
            if (!isGridContainerLayout(componentStageData)) {
                return
            }
            const heuristicToApply = findHeuristic(
                columnsHeuristics,
                componentStageData,
                contextHelper,
                readOnlyExtensionAPI
            )

            if (!heuristicToApply) {
                return
            }

            heuristicToApply.apply(componentStageData, contextHelper, readOnlyExtensionAPI)

            return {
                appliedHeuristics: [heuristicToApply.heuristicCode]
            }
        }

    const setContainerColumnsAdaptive: PluginStageHandler<ComponentStageData> = (
        componentStageData: ComponentStageData,
        ctx: PluginContext
    ) => {
        if (!isGridContainerLayout(componentStageData)) {
            return
        }
        const {initialSnapshot, componentPointer} = componentStageData
        const targetLayout = initialSnapshot.getValue(getTargetLayoutPointer(componentPointer))

        if (_.isEmpty(targetLayout?.containerLayout)) {
            return setContainerColumns(containerColumnsHeuristicsAdaptive)(componentStageData, ctx)
        }
    }

    const containerColumnsHeuristicsModes = {
        [AutoResponsivenessMode.Adaptive]: setContainerColumnsAdaptive,
        [AutoResponsivenessMode.Flush]: setContainerColumns(containerColumnsHeuristics)
    }

    const run: PluginStageHandler<ComponentStageData> = (
        componentStageData: ComponentStageData,
        contextHelper?: PluginContext
    ) => {
        const marginsHeuristicToApply = findHeuristicFromTree(
            horizontalSpacingTree,
            componentStageData,
            contextHelper,
            readOnlyExtensionAPI
        )
        if (!marginsHeuristicToApply) {
            return
        }
        marginsHeuristicToApply.apply(componentStageData, contextHelper, readOnlyExtensionAPI)
        const widthHeuristicToApply = findHeuristicFromTree(
            widthTree,
            componentStageData,
            contextHelper,
            readOnlyExtensionAPI
        )
        if (!widthHeuristicToApply) {
            return
        }
        widthHeuristicToApply.apply(componentStageData, contextHelper)

        return {
            appliedHeuristics: [marginsHeuristicToApply.heuristicCode, widthHeuristicToApply.heuristicCode]
        }
    }

    const register = (registry: PluginHeuristicsRegistry) => {
        registry.register(
            stages.HORIZONTAL_LAYOUT_SCALE,
            createAdaptiveHeuristic(run, updateContentBoxByExistingLayout)
        )
        registry.register(stages.HORIZONTAL_LAYOUT_SCALE, generatePluginHandlerPerMode(containerColumnsHeuristicsModes))
    }

    const createApi = (context: PluginContext): HorizontalScaleAndSizePluginAPI => {
        return {
            getWidth: (compPointer: Pointer): number => {
                return context.get(getContentBoxKey(compPointer))
            }
        }
    }

    const name = 'horizontalScaleAndSize'

    return {
        register,
        createApi,
        name,
        dependencies: ['verticalScaleAndSize']
    }
}
